import React from 'react'
import styled from 'styled-components'
import {
  cartDrawer,
  colors,
  marginSizes,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import { isObjectEmpty } from 'src/utils/object'
import { logErrorToRollbar } from '@jarvis.oddle/core/tools/rollbarHelper'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import modalController, {
  makeControlledModal
} from 'src/decorators/ModalController'
import Div from '@jarvis.oddle/core/components/Div'
import Flex from '@jarvis.oddle/core/components/Flex'
import Divider from '@jarvis.oddle/core/components/Divider'
import Modal, { CloseButton } from '@jarvis.oddle/core/components/Modal'
import { H3, H4, P } from '@jarvis.oddle/core/components/Typo'
import Loading from '@jarvis.oddle/core/components/Loading'
import IconBasketEmpty from '@jarvis.oddle/core/components/Icons/IconBasketEmpty'
import { getOrderItemQuantity } from 'src/utils/product'
import { redirect } from 'src/utils/routes'
import pickUpDeliveryFlow from 'src/flows/pickUpDeliveryFlow'
import SlideButton from 'src/components/molecules/SlideButton'
import { Address, Time } from 'src/components/molecules/CollectionDetails'
import StoreOrderSummary from 'src/components/molecules/StoreOrderSummary'
import CartProgressBars from 'src/components/molecules/CartProgressBar'
import OrderSection from './OrderSection'
import UserContext from 'src/containers/UserContext'
import useAppDispatch from 'src/hooks/useAppDispatcher'
import useAppSelector from 'src/hooks/useAppSelector'
import {
  createOrderItemsSelector,
  createPromotionalOrderItemsSelector
} from 'src/redux/order'
import {
  clearCartAction,
  fetchStoreOrderAction,
  getEligiblePromotionsAction
} from 'src/redux/order/action'
import { useRouter } from 'next/router'
import { selectDetailMenuSite } from 'src/redux/meta/selector'
import { useAppContext } from 'src/containers/AppContext'
import { selectOrder } from 'src/redux/order/selector'
import { isEmpty } from 'src/utils/string'
import useTracker from 'src/hooks/useTracker'

const Container = styled(Flex).attrs(() => ({
  flexDirection: 'column'
}))`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  height: 100%;
  width: 100%;
  background-color: ${(props) =>
    props.theme.colorBackgroundColor || colors.background};
  ${breakpoint('desktop')`
    width: 450px;
  `}
`

const Header = styled(Modal.Header)`
  flex-basis: ${cartDrawer.headerHeight};
  height: ${cartDrawer.headerHeight};
`

const Body = styled(Modal.Body)`
  ${breakpoint('desktop')`
    max-height: calc(100% - ${cartDrawer.headerHeight} - ${cartDrawer.footerHeight});
  `}
`

const StyledSection = styled(Modal.Section)`
  && {
    padding: 0;
    flex-grow: 1;
    display: flex;
  }
`

const Footer = styled(Modal.Footer)`
  height: ${cartDrawer.footerHeight};
  flex-basis: 96px;
  flex-shrink: 0;
`

interface CartDrawerProps {
  className: string
  modal: {
    show: boolean
    onClose: (callbacK: () => void) => void
    onClosed: () => void
  }
}

const CartDrawer = React.memo(
  ({ className, modal }: CartDrawerProps): JSX.Element => {
    const dispatch = useAppDispatch()
    const eligiblePromotions = useAppSelector(selectOrder).eligiblePromotions
    const storeOrder = useAppSelector(selectOrder).storeOrder
    const meta = useAppSelector(selectDetailMenuSite)
    const orderItems = useAppSelector((state) =>
      createOrderItemsSelector()(state)
    ) as Oms.V4.OrderItem[]
    const promotionalOrderItems = useAppSelector((state) =>
      createPromotionalOrderItemsSelector()(state)
    )
    const router = useRouter()
    const { t } = React.useContext(TranslationContext)
    const {
      formatFullDate,
      formatShortDate,
      prepareToCheckout,
      redirectToCheckout,
      formatMoney,
      tokenFromEats,
      setTokenFromEatsToNull
    } = useAppContext()
    const { login, fetchUserAction } = React.useContext(UserContext)
    const { notifyViewCart } = useTracker()

    const {
      isPickup,
      storeAddress,
      deliveryAddress,
      defaultStoreId,
      collectionDate,
      collectionTime
    } = storeOrder.data

    const isModalShowingRef = React.useRef(modal.show)
    React.useEffect(() => {
      isModalShowingRef.current = modal.show
    })

    const hasPromotions = async () => {
      // Temporary fix: before the API is revamped, getEligiblePromotionsAction should be called before the user
      // enter the CheckoutPromotionModal to prevent the cart resetting the promotions that have been selected
      const response = await dispatch(getEligiblePromotionsAction())

      // NOTE: Related to rollbar issue (FESC-730). Unable to replicate on local. We add a check here to report any error in calling getEligiblePromotionsAction to rollbar
      // TODO: Remove the below check once the root cause of the issue is known.
      if (response.meta.requestStatus === 'rejected') {
        logErrorToRollbar(
          'Error fetching eligible promotions',
          response.payload.error
        )
      }

      const { promoCodeEnabled, promotions } = response?.payload?.payload || {}
      return promoCodeEnabled || (promotions || []).length > 0
    }

    const reachedMinOrder =
      storeOrder.data.subtotal >= storeOrder.data.minimumOrder
    const shouldReachedMinOrder = meta?.preventCheckoutIfBelowMinimumAmount

    const itemsCount = storeOrder.data.orderItems
      ? getOrderItemQuantity(storeOrder.data.orderItems)
      : 0
    const isItemsEmpty = itemsCount === 0

    // pickUpDeliveryFlow will persist the flow state and check if the collection time is in tune with backend.
    // The null defaultStoreId value is received from backend and is required by backend to indicate that the store
    // is really set by the user (see storefrontApiServiceV3.java at oddlefnb)
    const isAppointmentReady =
      (isPickup && !!storeAddress && !defaultStoreId) ||
      (!isPickup && !!deliveryAddress)
    const isOrderItemsEmpty = isObjectEmpty(orderItems) || orderItems.length < 1

    const buttonText = React.useMemo(() => {
      if (
        storeOrder.status === 'pending' ||
        eligiblePromotions.status === 'pending'
      ) {
        return <Loading secondary />
      }
      if (!isAppointmentReady) {
        return t('Select your dining preference')
      }
      if (isOrderItemsEmpty) {
        return t('Start Ordering')
      }
      if (shouldReachedMinOrder && !reachedMinOrder) {
        return t('Min subtotal of _minimumOrder_ not met', {
          minimumOrder: formatMoney(storeOrder.data.minimumOrder)
        })
      }
      return [
        t('Order for _date_ _time_', {
          date: formatShortDate(collectionDate),
          time: collectionTime.split(' to ')[0] // NOTE: Results from API are always in HH:MM AM to HH:MM PM format
        }),
        t('Proceed to Checkout')
      ]
    }, [
      collectionDate,
      collectionTime,
      eligiblePromotions.status,
      formatMoney,
      formatShortDate,
      isAppointmentReady,
      isOrderItemsEmpty,
      reachedMinOrder,
      shouldReachedMinOrder,
      storeOrder.data.minimumOrder,
      storeOrder.status,
      t
    ])

    React.useEffect(() => {
      if (isAppointmentReady) {
        pickUpDeliveryFlow.updateMeta({ isVisited: true })
      }
    }, [isAppointmentReady])

    const goToPromotionOrCheckoutStep = async () => {
      const hasPromotion = await hasPromotions()
      if (hasPromotion && isModalShowingRef.current) {
        return modal.onClose(() =>
          modalController.showModal('CheckoutPromotionModal')
        )
      }
      if (hasPromotion) {
        return modalController.showModal('CheckoutPromotionModal')
      }
      if (isModalShowingRef.current) {
        return prepareToCheckout({
          onReadyToCheckout: (storeOrder) =>
            modal.onClose(() => redirectToCheckout(storeOrder.id)),
          onNotReadyToCheckout: (errors, errorCodes) =>
            modal.onClose(() =>
              modalController.showModal('CartErrorModal', {
                errors,
                errorCodes
              })
            ),
          onShouldRefresh: modal.onClose
        })
      }
      return prepareToCheckout({
        onReadyToCheckout: (storeOrder) => redirectToCheckout(storeOrder.id),
        onNotReadyToCheckout: (errors, errorCodes) =>
          modalController.showModal('CartErrorModal', {
            errors,
            errorCodes
          }),
        onShouldRefresh: () => undefined
      })
    }

    const goToNextStep = async () => {
      // log in from eats to shop if user click proceed to checkout
      if (tokenFromEats) {
        window.localStorage.setItem('accessToken', tokenFromEats)
        setTokenFromEatsToNull()
      }
      // should fetch new user's data before go to next step of checkout to make sure user still in session when checkout
      const userData = await fetchUserAction({})
      if (!isAppointmentReady) {
        return modal.onClose(() => {
          redirect({
            url: '/[locale]',
            as: `/${router.query.locale}?selectDiningPreference=true`,
            scroll: false,
            shallow: true
          })
          pickUpDeliveryFlow.start({
            onFinished: () => modalController.showModal('CartDrawer')
          })
        })
      }
      if (isOrderItemsEmpty) {
        const isMenuPath = router.pathname !== '/[locale]'
        return modal.onClose(
          () =>
            !isMenuPath &&
            redirect({
              url: '/[locale]',
              as: `/${router.query.locale}`,
              shallow: true
            })
        )
      }

      const userDataResponse = (
        userData.payload
          ? { success: userData.success, error: userData.payload.errorCode }
          : userData
      ) as {
        error: string
        success: boolean
      }

      if (
        userDataResponse.error === 'customer_require_verification' ||
        (isEmpty(userDataResponse.error) && userDataResponse.success)
      ) {
        return goToPromotionOrCheckoutStep()
      }
      if (window.opw) {
        return modal.onClose(() =>
          login(undefined, { goToPromotionOrCheckoutStep: true })
        )
      }
      modal.onClose(() => login({ onFinished: goToPromotionOrCheckoutStep }))
    }

    const editAppointmentAddress = () => {
      modal.onClose(() =>
        pickUpDeliveryFlow.start({
          type: isPickup ? 'pickupStoreSelection' : 'deliverySelectAddress',
          onFinished: () => modalController.showModal('CartDrawer')
        })
      )
    }

    const editAppointmentTime = () => {
      modal.onClose(() =>
        pickUpDeliveryFlow.start({
          type: 'dateTimeSelection',
          onFinished: () => modalController.showModal('CartDrawer')
        })
      )
    }

    const onEnter = async () => {
      // sync the latest state of the current store order with OMS
      // everytime the Cart Drawer is shown
      const data = await dispatch(fetchStoreOrderAction())
      await dispatch(getEligiblePromotionsAction())
      if (data?.payload?.payload?.storeOrder) {
        notifyViewCart(data.payload.payload.storeOrder)
      }
    }

    return (
      <Modal
        {...modal}
        initialFocus='#slideButton'
        onEnter={onEnter}
        slideInAnimation='right'
      >
        <Container className={className}>
          <Header>
            <Flex flexDirection='column' alignItems='center'>
              <H3 bold normalised>
                {t('Your Cart')}
              </H3>
              {!isOrderItemsEmpty && !isItemsEmpty && (
                <P data-testid='cartDrawer.itemCountSummary' normalised>
                  {t(`You've added _number_ items`, { number: itemsCount })}
                </P>
              )}
            </Flex>
            <CloseButton onClick={modal.onClose} />
          </Header>
          <Body>
            {isAppointmentReady && (
              <Modal.Section>
                <Address
                  storeOrder={storeOrder}
                  onClick={editAppointmentAddress}
                />
                <Divider />
                <Time
                  storeOrder={storeOrder}
                  formatFullDate={formatFullDate}
                  onClick={editAppointmentTime}
                />
              </Modal.Section>
            )}
            <StyledSection flexGrow={1}>
              {isOrderItemsEmpty ? (
                <Flex
                  data-testid='cartDrawer.emptyCart'
                  flexDirection='column'
                  justifyContent='center'
                  alignItems='center'
                  flexGrow={1}
                >
                  <IconBasketEmpty width='130px' height='130px' />
                  <H3 bold normalised marginTop={marginSizes.medium}>
                    {t('Your cart is empty')}
                  </H3>
                  <P normalised>
                    {isItemsEmpty
                      ? t('Looks like you have no items in your cart')
                      : t('Begin your order by selecting a service')}
                  </P>
                </Flex>
              ) : (
                <React.Fragment>
                  {isAppointmentReady && (
                    <React.Fragment>
                      <CartProgressBars />
                      <Divider />
                    </React.Fragment>
                  )}
                  <Div
                    paddingLeft={paddingSizes.medium}
                    paddingRight={paddingSizes.medium}
                    paddingTop={paddingSizes.small}
                  >
                    <OrderSection
                      orderItems={orderItems}
                      promotionalOrderItems={promotionalOrderItems}
                      clearCartAction={() => dispatch(clearCartAction())}
                    />
                    <StoreOrderSummary
                      storeOrder={storeOrder.data}
                      isAppointmentReady={isAppointmentReady}
                      shouldShowTotal={false}
                      shouldShowVoucherSummary={false}
                      shouldShowSurchargeWarning={!shouldReachedMinOrder}
                    />
                  </Div>
                </React.Fragment>
              )}
            </StyledSection>
          </Body>
          <Footer>
            <Flex
              width='100%'
              flexDirection='column'
              justifyContent='center'
              alignItems='center'
            >
              {!isItemsEmpty && (
                <Flex
                  width='100%'
                  flexDirection='row'
                  justifyContent='space-between'
                  marginBottom={marginSizes.small}
                >
                  <H4 bold normalised>
                    {t('Total')}
                  </H4>
                  <H4 data-testid='cartDrawer.totalAmount' bold normalised>
                    {formatMoney(itemsCount > 0 ? storeOrder.data.total : 0)}
                  </H4>
                </Flex>
              )}
              <SlideButton
                onClick={goToNextStep}
                width='100%'
                disabled={
                  storeOrder.status === 'pending' ||
                  (isAppointmentReady &&
                    !isOrderItemsEmpty &&
                    shouldReachedMinOrder &&
                    !reachedMinOrder)
                }
              >
                {buttonText}
              </SlideButton>
            </Flex>
          </Footer>
        </Container>
      </Modal>
    )
  }
)

export default makeControlledModal(CartDrawer, 'CartDrawer')
